import React, { useEffect, useState } from "react";
import { api, rupiah } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";
import { TrendingUp, ShoppingBag, Wallet, Boxes, Loader2 } from "lucide-react";

const PAY_LABEL = { cash: "Tunai", qris: "QRIS", transfer: "Transfer", debit: "Debit/Kredit" };

const Metric = ({ icon: Icon, label, value, testid }) => (
  <Card className="p-5 border-slate-200 rounded-xl shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="w-9 h-9 rounded-lg bg-accent text-primary flex items-center justify-center"><Icon className="w-4 h-4" /></div>
    </div>
    <div className="mt-3 font-heading text-2xl font-bold tracking-tight">{value}</div>
  </Card>
);

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10);
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);

  const load = () => {
    setData(null);
    api.get("/reports/sales", { params: { date_from: from, date_to: to } }).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [from, to]);

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Laporan Penjualan</h1>
          <p className="text-muted-foreground text-sm">Analisa performa toko Anda.</p>
        </div>
        <div className="flex items-end gap-2">
          <div><label className="text-xs text-muted-foreground">Dari</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-lg mt-1" data-testid="report-from" /></div>
          <div><label className="text-xs text-muted-foreground">Sampai</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg mt-1" data-testid="report-to" /></div>
        </div>
      </div>

      {!data ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric icon={TrendingUp} label="Total Penjualan" value={rupiah(data.total_sales)} testid="metric-total-sales" />
            <Metric icon={ShoppingBag} label="Jumlah Transaksi" value={data.total_transactions} testid="metric-txn-count" />
            <Metric icon={Wallet} label="Rata-rata / Transaksi" value={rupiah(data.average_transaction)} testid="metric-avg" />
            <Metric icon={Boxes} label="Item Terjual" value={data.items_sold} testid="metric-items" />
          </div>

          <Card className="p-5 border-slate-200 rounded-xl shadow-sm">
            <h3 className="font-heading font-bold">Tren Penjualan Harian</h3>
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.series} margin={{ left: -10, right: 10, top: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                  <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                  <Line type="monotone" dataKey="total" stroke="hsl(21 90% 48%)" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid lg:grid-cols-2 gap-5">
            <Card className="p-5 border-slate-200 rounded-xl shadow-sm">
              <h3 className="font-heading font-bold">Produk Terlaris</h3>
              <div className="h-64 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.top_products.slice(0, 6)} layout="vertical" margin={{ left: 10, right: 10 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                    <Bar dataKey="revenue" fill="hsl(21 90% 48%)" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5 border-slate-200 rounded-xl shadow-sm">
              <h3 className="font-heading font-bold">Rincian per Metode Bayar</h3>
              <div className="mt-4 space-y-3">
                {data.payment_breakdown.map((p) => {
                  const pct = data.total_sales ? (p.total / data.total_sales) * 100 : 0;
                  return (
                    <div key={p.method}>
                      <div className="flex justify-between text-sm mb-1"><span>{PAY_LABEL[p.method] || p.method}</span><span className="font-semibold">{rupiah(p.total)}</span></div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
                {data.payment_breakdown.length === 0 && <p className="text-sm text-muted-foreground">Belum ada data.</p>}
              </div>
            </Card>
          </div>

          <Card className="border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-5 pb-0"><h3 className="font-heading font-bold">Tabel Produk Terlaris</h3></div>
            <div className="overflow-x-auto kasir-scroll mt-3">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-muted-foreground">
                  <tr className="text-left"><th className="py-3 px-4 font-medium">Produk</th><th className="py-3 px-4 font-medium text-right">Qty Terjual</th><th className="py-3 px-4 font-medium text-right">Pendapatan</th></tr>
                </thead>
                <tbody>
                  {data.top_products.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100"><td className="py-2.5 px-4 font-medium">{p.name}</td><td className="py-2.5 px-4 text-right">{p.qty}</td><td className="py-2.5 px-4 text-right font-semibold">{rupiah(p.revenue)}</td></tr>
                  ))}
                  {data.top_products.length === 0 && <tr><td colSpan={3} className="py-10 text-center text-muted-foreground">Belum ada penjualan.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
